import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../App";
import axios from "axios";
import { Plus, Trash2, Wallet, CreditCard, Banknote, FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

const odemeYontemleri = [
  { value: "nakit", label: "Nakit", icon: Banknote },
  { value: "kart", label: "Kredi Kartı", icon: CreditCard },
  { value: "havale", label: "Havale/EFT", icon: Wallet },
  { value: "cek", label: "Çek", icon: FileText }
];

const Tahsilatlar = () => {
  const [items, setItems] = useState([]);
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    musteri_adi: "",
    tutar: "",
    odeme_yontemi: "nakit",
    tarih: new Date(),
    aciklama: "",
    cari_hesap_id: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tahsilatRes, cariRes] = await Promise.all([
        axios.get(`${API}/tahsilatlar`, { withCredentials: true }),
        axios.get(`${API}/cari-hesaplar`, { withCredentials: true })
      ]);
      setItems(tahsilatRes.data);
      setCariHesaplar(cariRes.data.filter(c => c.tip === "alacak" && !c.odendi));
    } catch (error) {
      toast.error("Veriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const handleCariSelect = (cariId) => {
    if (cariId === "none") {
      setForm({ ...form, cari_hesap_id: "", musteri_adi: "", tutar: "" });
      return;
    }
    
    const cari = cariHesaplar.find(c => c.id === cariId);
    if (cari) {
      setForm({
        ...form,
        cari_hesap_id: cariId,
        musteri_adi: cari.musteri_adi,
        tutar: cari.tutar.toString()
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.musteri_adi || !form.tutar) {
      toast.error("Lütfen gerekli alanları doldurun");
      return;
    }

    try {
      await axios.post(`${API}/tahsilatlar`, {
        ...form,
        tutar: parseFloat(form.tutar),
        tarih: form.tarih.toISOString(),
        cari_hesap_id: form.cari_hesap_id || null
      }, { withCredentials: true });
      
      toast.success("Tahsilat kaydedildi");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Kayıt eklenirken hata oluştu");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu tahsilatı silmek istediğinize emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/tahsilatlar/${id}`, { withCredentials: true });
      toast.success("Tahsilat silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const resetForm = () => {
    setForm({
      musteri_adi: "",
      tutar: "",
      odeme_yontemi: "nakit",
      tarih: new Date(),
      aciklama: "",
      cari_hesap_id: ""
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(value || 0);
  };

  const getOdemeIcon = (yontem) => {
    const method = odemeYontemleri.find(m => m.value === yontem);
    if (method) {
      const Icon = method.icon;
      return <Icon size={16} className="text-[#5E5E62]" />;
    }
    return null;
  };

  const toplamTahsilat = items.reduce((sum, i) => sum + i.tutar, 0);
  const buAyTahsilat = items.filter(i => {
    const tarih = new Date(i.tarih);
    const simdi = new Date();
    return tarih.getMonth() === simdi.getMonth() && tarih.getFullYear() === simdi.getFullYear();
  }).reduce((sum, i) => sum + i.tutar, 0);

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="tahsilatlar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#0A0A0B] tracking-tight">
              Tahsilatlar
            </h1>
            <p className="text-[#5E5E62] text-sm mt-1">
              Tüm tahsilatlarınızı kaydedin ve takip edin
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button className="btn-primary flex items-center gap-2" data-testid="add-tahsilat-btn">
                <Plus size={18} />
                Yeni Tahsilat
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-heading">Yeni Tahsilat</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Cari Hesap Seçimi */}
                {cariHesaplar.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">
                      Cari Hesaptan Tahsilat (Opsiyonel)
                    </label>
                    <Select 
                      value={form.cari_hesap_id || "none"} 
                      onValueChange={handleCariSelect}
                    >
                      <SelectTrigger data-testid="cari-select">
                        <SelectValue placeholder="Seçiniz" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Manuel Giriş</SelectItem>
                        {cariHesaplar.map((cari) => (
                          <SelectItem key={cari.id} value={cari.id}>
                            {cari.musteri_adi} - {formatCurrency(cari.tutar)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Müşteri Adı */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Müşteri / Firma Adı</label>
                  <input
                    type="text"
                    value={form.musteri_adi}
                    onChange={(e) => setForm({ ...form, musteri_adi: e.target.value })}
                    className="input-default"
                    placeholder="Müşteri veya firma adı"
                    data-testid="musteri-input"
                  />
                </div>

                {/* Tutar */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Tutar (₺)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.tutar}
                    onChange={(e) => setForm({ ...form, tutar: e.target.value })}
                    className="input-default"
                    placeholder="0.00"
                    data-testid="tutar-input"
                  />
                </div>

                {/* Ödeme Yöntemi */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Ödeme Yöntemi</label>
                  <Select value={form.odeme_yontemi} onValueChange={(value) => setForm({ ...form, odeme_yontemi: value })}>
                    <SelectTrigger data-testid="odeme-yontemi-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {odemeYontemleri.map((yontem) => (
                        <SelectItem key={yontem.value} value={yontem.value}>
                          <div className="flex items-center gap-2">
                            <yontem.icon size={16} />
                            {yontem.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Tarih */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Tarih</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="input-default text-left" data-testid="tarih-picker">
                        {format(form.tarih, "d MMMM yyyy", { locale: tr })}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.tarih}
                        onSelect={(date) => date && setForm({ ...form, tarih: date })}
                        locale={tr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Açıklama */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Açıklama</label>
                  <input
                    type="text"
                    value={form.aciklama}
                    onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
                    className="input-default"
                    placeholder="Opsiyonel"
                    data-testid="aciklama-input"
                  />
                </div>

                <button type="submit" className="btn-primary w-full" data-testid="submit-tahsilat-btn">
                  Kaydet
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Wallet size={18} className="text-[#00C566]" />
              <span className="text-sm text-[#5E5E62]">Toplam Tahsilat</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#00C566]">{formatCurrency(toplamTahsilat)}</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Wallet size={18} className="text-[#002FA7]" />
              <span className="text-sm text-[#5E5E62]">Bu Ay Tahsilat</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#002FA7]">{formatCurrency(buAyTahsilat)}</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tahsilatlar-table">
              <thead>
                <tr className="border-b border-[#E4E4E7]">
                  <th className="table-header">Müşteri/Firma</th>
                  <th className="table-header">Tutar</th>
                  <th className="table-header">Ödeme Yöntemi</th>
                  <th className="table-header">Tarih</th>
                  <th className="table-header">Açıklama</th>
                  <th className="table-header w-16"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-[#5E5E62]">
                      Henüz tahsilat bulunmuyor
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id} className="table-row">
                      <td className="table-cell font-medium">{item.musteri_adi}</td>
                      <td className="table-cell font-mono text-[#00C566]">
                        +{formatCurrency(item.tutar)}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          {getOdemeIcon(item.odeme_yontemi)}
                          <span className="capitalize">{item.odeme_yontemi}</span>
                        </div>
                      </td>
                      <td className="table-cell font-mono text-[#5E5E62]">
                        {new Date(item.tarih).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="table-cell text-[#5E5E62]">{item.aciklama || "-"}</td>
                      <td className="table-cell">
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-[#FF3B30] hover:bg-[#FFEBEE] rounded-sm transition-colors"
                          data-testid={`delete-${item.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Tahsilatlar;
